<template>
  <div class="home">

    <div class="uk-section uk-section-small uk-background-legible uk-background-cover uk-background-fixed uk-background-blend-multiply hero page-header" uk-height-viewport v-bind:style="`background-image: url('${backgroundImage}')`">

        <div class="uk-light hero-content-home content-max uk-position-center uk-width-1-1" v-if="heroClass">
            <div class="uk-padding-large uk-flex">
                <div class="hero-inner" v-bind:class="heroClass">
                    <h1 v-html="headline"></h1>
                    <p class="uk-text-lead">{{summary}}</p>

                    <router-link v-for="btn in cta" :to="btn.link" class="uk-button theme-button" :class="btn.class">{{btn.title}}</router-link>

                    <FollowLinks class="follow-buttons uk-margin-large-top" />

                </div>

            </div>
        </div>
    </div>

    <slot name="top">
        <CTA :feature="page.topFeature" v-if="page.topFeature && page.topFeature.id > 0" class="uk-box-shadow-medium" />
    </slot>

    <div class="uk-section uk-section-medium uk-background-white">
        <div class="uk-container">
            <div class="uk-flex" uk-grid>
                <div class="uk-width-2-5@s uk-width-1-3@m">
                    <img :src="theme + '/app/assets/images/unsung_hero_dictionary.svg'" alt="Unsung Hero" width="300" class="uk-margin-top uk-margin-bottom-small" />
                    <h4><b>1</b> An Unsung Hero is an individual whose actions made a profound and positive impact on history, but is largely unrecognized by contemporary generations. The actions which define the Unsung Hero’s legacy as a role model must have occurred a minimum of 20 years in the past.</h4>
                </div>
                <div class="uk-width-3-5@s uk-width-2-3@m">
                    <div class="uk-card uk-card-default">
                        <div class="flex-video widescreen uk-margin-remove-bottom">
                            <iframe width="560" height="315" src="https://www.youtube.com/embed/sPnHcl3GbbI" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="uk-section uk-section-medium uk-background-muted uk-preserve-color">
        <div class="uk-container">
            <div class="uk-text-center uk-light">
                <h1>Meet Our Heroes</h1>
                <p class="uk-text-lead">Ordinary people who have done the extraordinary.</p>
            </div>

            <template v-if="!projectsLoading">
                <div class="uk-grid uk-child-width-1-3@m" uk-grid uk-height-match="target: .match-height">
                    <!--  uk-height-match=".uk-card" -->
                    <div v-for="project in featuredProjects" :key="project.urlSegment">
                        <ProjectCard :project="project" />
                    </div>
                </div>

                <div class="uk-text-center uk-margin-large-top">
                    <router-link to="/programs/projects" class="text-orange" >
                        <h3 class="text-orange branded">Browse all <br class="uk-hidden@m" /><span>Unsung Hero Projects</span> <span class="uk-icon text-orange" uk-icon="chevron-right"></span></h3>
                    </router-link>
                </div>
            </template>
            <div class="uk-padding-large uk-text-center uk-light" v-else >
                <i class="uk-margin-small-right" uk-spinner></i> Loading <b>Heroes</b>...
            </div>
        </div>
    </div>

    <FloatingBadge text="Over 3 million students reached worldwide" badgeClass="uk-badge-default"/>

    <div class="uk-section uk-section-medium uk-background-primary uk-text-center uk-light">
        <h2>Bring Unsung Heroes to Your Classroom</h2>
    </div>

    <div class="uk-grid uk-grid-collapse uk-child-width-1-2@s" uk-grid>
        <div>
            <div class="ar4x3 uk-background-cover uk-background-light uk-background-blend-multiply promo-box-1">
                <div class="uk-light uk-position-center uk-padding uk-text-center">
                    <h2>Resources for Teachers</h2>
                    <p>Lesson Plans & More</p>
                    <router-link class="uk-button theme-button button-green" to="/curriculum/lesson-plans">Inspire Your Class</router-link>
                </div>
            </div>
        </div>
        <div>
            <div class="ar4x3 uk-background-cover uk-background-light uk-background-blend-multiply promo-box-2">
                <div class="uk-light uk-position-center uk-padding uk-text-center">
                    <h2>Student Competitions</h2>
                    <p>The Discovery Award & ArtEffect Project</p>
                    <router-link class="uk-button theme-button button-orange" to="/competitions">Explore the Entries</router-link>
                </div>
            </div>
        </div>
    </div>

    <router-link to="/museum" >
    <div class="uk-text-center uk-background-light uk-background-cover uk-background-fixed uk-position-relative ar5x2 uk-height-viewport-small" v-bind:style="{ backgroundImage: `url(${museumImage}` }">
        <div class="uk-light uk-position-top">
            <h3 class="uk-background-muted uk-display-inline-block top-badge"><b>Visit Our Museum</b> in Fort Scott, Kansas</h3>
        </div>
    </div>
    </router-link>
    
    <slot name="bottom">
        <CTA :feature="page?.bottomFeature" v-if="page?.bottomFeature && page.bottomFeature.id > 0" />
    </slot>

  </div>
</template>

<script>
import CTA from '../components/CTA.vue'
import ProjectCard from '../projects/ProjectCard.vue'
import FloatingBadge  from '../components/FloatingBadge.vue'
import gql from 'graphql-tag'

import unsungHeroes from '../assets/images/hall_unsung_heroes@2x.jpg'
import unsungHeroesSmall from '../assets/images/hall_unsung_heroes.jpg'
import heroImage from '../assets/images/home_hero.jpg'
import heroImageLarge from '../assets/images/home_hero@2x.jpg'

// GraphQL query
const featuredProjectsQuery = gql`{
    readProjects(
        filter: {
            featured: { eq: true }
        }
    ) {
        nodes {
            id
            title
            link
            summary
            image {
                medium4x3
            }
            category {
                id
                title
            }
        }
    }
}`

export default {
    props: ['page',],
    inject: ['theme', 'state', '$width'],
    beforeMount() {
        this.state.api.subnav = []
    },
    data() {
        return {
            headline: this.page.subtitle,
            summary: this.page.summary,
            cta: [{
                    title: "Heroes",
                    class: "uk-button-primary uk-width-1-1@s",
                    link: '/programs/projects'
                },
                {
                    title: "Competitions",
                    class: "button-orange uk-width-1-1@s",
                    link: '/competitions'
                },
                {
                    title: "Lesson Plans",
                    class: "button-green uk-width-1-1@s",
                    link: '/curriculum/lesson-plans'
                }
            ],
            featuredProjects: [],
            loading: false,
            projectsLoading: false,
            readOneHomePage: {}
        }
    },
    components: {
        CTA,
        ProjectCard,
        FloatingBadge
    },
    computed: {
        heroClass() {
            if(this.page) {
                let heroClass = this.page.heroClass

                if(heroClass=="Left") {
                    return "uk-width-3-5@l uk-width-4-5@m uk-text-left"
                }
                if(heroClass=="Right") {
                    return "uk-width-3-5@l uk-push-2-5@l uk-width-4-5@m uk-push-1-5@m uk-text-right"
                }

                return "uk-width-2-3@l uk-width-4-5@m uk-text-center uk-align-center"
            }
        },
        backgroundImage() {
            if(this.page && !!this.page.heroImage.large4x3) {
                if(this.$width<900) {
                    return this.page.heroImage.medium
                }
                return this.page.heroImage.large4x3
            }
            else {
                if(this.$width<1000) {
                    return heroImage
                }
                else {
                    return heroImageLarge
                }
            }
        },
        museumImage() {
            
            if(this.$width<640) {
                return unsungHeroesSmall
            }

            return unsungHeroes
        }
    },
    apollo: {
        featuredProjects: {
            query: featuredProjectsQuery,
            loadingKey: 'projectsLoading',
            update(data) { return data.readProjects.nodes }
        }
    }
}
</script>
